import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { getVersionInfo } from "../lib/configMgr";

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const currentVersion = getVersionInfo().version?.number;

// Compares dotted numeric versions (e.g. "9.1.278"). Returns true only if `a` is
// strictly newer than `b`. A stale CDN-cached /versionInfo.json response can report
// an OLDER version than what's actually running — that must never be treated as
// "an update is available" (WEBAPP: false "9.1.278 → 9.1.275" toast).
const isNewerVersion = (a, b) => {
  const partsA = String(a).split(".").map(Number);
  const partsB = String(b).split(".").map(Number);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const numA = partsA[i] || 0;
    const numB = partsB[i] || 0;
    if (numA !== numB) return numA > numB;
  }
  return false;
};

/**
 * Polls /versionInfo.json every 5 minutes. When a new version is detected,
 * shows a dismissible "update available" toast rather than reloading —
 * silently reloading on the next idle moment (e.g. right after the user hits
 * Stop) is disorienting, and a false-positive mismatch (stale CDN cache,
 * transient fetch race) previously left the app stuck reloading forever
 * since the pending flag was never cleared once a mismatch was seen.
 *
 * @param {boolean} isAdmin - show the current/pending version numbers (admins only)
 */
export const useVersionCheck = (isAdmin = false) => {
  const notifiedVersion = useRef(null);

  useEffect(() => {
    if (!currentVersion) return;

    const check = async () => {
      try {
        const res = await fetch(`/versionInfo.json?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const latest = data?.version?.number;
        // Only notify once per newly-observed version — re-checks that still see the
        // same mismatch (or a resolved one) must not keep re-toasting/re-arming.
        if (latest && isNewerVersion(latest, currentVersion) && latest !== notifiedVersion.current) {
          notifiedVersion.current = latest;
          const message = isAdmin
            ? `Update available: ${currentVersion} → ${latest}`
            : "Update available";
          toast.info(message, {
            autoClose: false,
            closeOnClick: false,
            onClick: () => window.location.reload(),
          });
        } else if (latest && !isNewerVersion(latest, currentVersion)) {
          // No longer newer (matches, or was a stale/older CDN-cached response) —
          // allow a future genuine update to notify again rather than staying latched.
          notifiedVersion.current = null;
        }
      } catch {
        // network error — ignore, try again next interval
      }
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAdmin]);
};
